import { getMemberId } from '@trello/authentication';
import { sendErrorEvent } from '@trello/error-reporting';
import { TrelloStorage } from '@trello/storage';

import type { AccessibleProductsResponse } from './getAccessibleProducts';

const DAY_IN_MS = 86_400_000;
/** Random window (up to 4 hours) added on top of the 24h base TTL to reduce cache stampedes */
const FOUR_HOURS_IN_MS = 14_400_000;

const getCacheKey = (): `accessibleProducts-${string}` =>
  `accessibleProducts-${getMemberId()}`;

const getJiraCacheKey = (): `accessibleJiraProducts-${string}` =>
  `accessibleJiraProducts-${getMemberId()}`;

export const getCachedAccessibleProducts: () =>
  | AccessibleProductsResponse['products']
  | null = () => {
  try {
    const accessibleProductsStorage = TrelloStorage.get(getCacheKey());
    if (accessibleProductsStorage) {
      if (typeof accessibleProductsStorage.expiration === 'number') {
        return accessibleProductsStorage.expiration >= Date.now()
          ? accessibleProductsStorage?.data?.products
          : null;
      } else {
        throw new Error('Unexpected localStorage timestamp type');
      }
    } else {
      return null;
    }
  } catch (error) {
    sendErrorEvent(error, {
      tags: {
        ownershipArea: 'trello-electric',
        feature: 'Cross Flow Provider',
      },
    });
    return null;
  }
};

export const cacheAccessibleProducts = (
  data: AccessibleProductsResponse,
): void => {
  // add a random amount (up to ~2.7 hours) to avoid cache stampede in the morning
  const TIMESTAMP_EXPIRATION_DURATION_IN_MS =
    DAY_IN_MS + Math.floor(Math.random() * 10_000_000);
  TrelloStorage.set(getCacheKey(), {
    expiration: Date.now() + TIMESTAMP_EXPIRATION_DURATION_IN_MS,
    data,
  });
};

export const clearCachedAccessibleProducts = (): void => {
  TrelloStorage.unset(getCacheKey());
};

export const getCachedAccessibleJiraProducts: () =>
  | AccessibleProductsResponse['products']
  | null = () => {
  try {
    const storage = TrelloStorage.get(getJiraCacheKey());
    if (storage) {
      if (typeof storage.expiration === 'number') {
        return storage.expiration >= Date.now()
          ? storage?.data?.products
          : null;
      } else {
        throw new Error('Unexpected localStorage timestamp type');
      }
    }
    return null;
  } catch (error) {
    sendErrorEvent(error, {
      tags: {
        ownershipArea: 'trello-electric',
        feature: 'Cross Flow Provider',
      },
    });
    return null;
  }
};

export const cacheAccessibleJiraProducts = (
  data: AccessibleProductsResponse,
): void => {
  const expirationMs = DAY_IN_MS + Math.floor(Math.random() * FOUR_HOURS_IN_MS);
  TrelloStorage.set(getJiraCacheKey(), {
    expiration: Date.now() + expirationMs,
    data,
  });
};

export const clearCachedAccessibleJiraProducts = (): void => {
  TrelloStorage.unset(getJiraCacheKey());
};
